import React, { useState } from "react";
import { Box } from "@mui/system";
import TypographyInline from "../../../../../components/Typography/TypographyInline";
import IconComponent from "../../../../../components/Icon/IconComponent";
import {
  GREY,
  BORDER,
  BASE,
  ACTIONS,
  PRIMARY,
} from "../../../../../utils/constants/colors";
import { fontWeight, ICON_LABELS } from "../../../../../utils/constants/UI";
import { STRINGS } from "../../strings";
import { CAMPAIGN_CREATE_DAYS_LIST } from "../../../../../utils/constants";

const SENDER_COLLAPSE_LIMIT = 3;
const AVATAR_GROUP_LIMIT = 4;
const KEY_COLLAPSE_LIMIT = 8;
const initialOf = (email) => email?.trim()?.charAt(0) || "?";

const CARD_STYLE = {
  border: `1px solid ${BORDER.DEFAULT}`,
  borderRadius: "8px",
  background: BASE.WHITE,
  overflow: "hidden",
};

const CARD_HEADER_STYLE = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  padding: "10px 14px",
  borderBottom: `1px solid ${BORDER.LIGHT}`,
};

const CARD_HEADER_LEFT_STYLE = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  minWidth: 0,
};

const STEP_BADGE_STYLE = {
  height: "22px",
  padding: "0 10px",
  borderRadius: "11px",
  background: ACTIONS.DEFAULT,
  color: GREY.SECONDARY,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "11px",
  fontWeight: 700,
  flexShrink: 0,
  letterSpacing: "0.02em",
};

const EDIT_BTN_STYLE = {
  display: "inline-flex",
  alignItems: "center",
  gap: "4px",
  padding: "4px 8px",
  border: "none",
  background: "transparent",
  cursor: "pointer",
  borderRadius: "4px",
};

const CARD_BODY_STYLE = {
  padding: "10px 14px",
  display: "flex",
  flexDirection: "column",
  gap: "8px",
};

const ROW_STYLE = {
  display: "flex",
  alignItems: "flex-start",
  gap: "16px",
};

const LABEL_COL_STYLE = {
  width: "140px",
  flexShrink: 0,
  paddingTop: "1px",
};

const VALUE_COL_STYLE = {
  flex: 1,
  minWidth: 0,
};

const SENDER_ROW_STYLE = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "16px",
  padding: "6px 0",
  borderBottom: `1px solid ${BORDER.LIGHT}`,
};

const DAY_PILL_STYLE = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  padding: "4px 10px",
  background: ACTIONS.HOVER,
  border: `1px solid ${BORDER.LIGHT}`,
  borderRadius: "6px",
};

const KEY_PILL_STYLE = {
  display: "inline-flex",
  alignItems: "center",
  padding: "3px 8px",
  background: ACTIONS.HOVER,
  border: `1px solid ${BORDER.LIGHT}`,
  borderRadius: "4px",
};

// Selected customization keys as {{tag}} pills. Collapsed to one line's worth
// by default so a 20-key campaign doesn't push the rest of the preview off.
const SelectedKeyList = ({ keys, selectedIds }) => {
  const [expanded, setExpanded] = useState(false);
  const selectedSet = new Set(selectedIds || []);
  const selected = (keys || []).filter((item) => selectedSet.has(item.key));

  if (!selected.length) {
    return <ValueText value="" placeholder="No keys selected" />;
  }

  const visible = expanded ? selected : selected.slice(0, KEY_COLLAPSE_LIMIT);
  const overflow = selected.length - visible.length;

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
      {visible.map((item) => (
        <Box key={item.key} style={KEY_PILL_STYLE}>
          <TypographyInline
            label={`{{${item.key}}}`}
            size="xs"
            color={GREY.SECONDARY}
            fontWeight={fontWeight.MEDIUM}
          />
        </Box>
      ))}
      {overflow > 0 || expanded ? (
        <Box
          sx={{ cursor: "pointer", display: "inline-flex", alignItems: "center" }}
          onClick={() => setExpanded((v) => !v)}
        >
          <TypographyInline
            label={expanded ? "Show less" : `+${overflow} more`}
            size="xs"
            color={PRIMARY.W_500}
            fontWeight={fontWeight.MEDIUM}
          />
        </Box>
      ) : null}
    </Box>
  );
};

const StepCard = ({ stepNumber, title, onEdit, children }) => (
  <Box style={CARD_STYLE}>
    <Box style={CARD_HEADER_STYLE}>
      <Box style={CARD_HEADER_LEFT_STYLE}>
        <Box style={STEP_BADGE_STYLE}>{`Step ${stepNumber}`}</Box>
        <TypographyInline
          label={title}
          size="sm"
          color={GREY.PRIMARY}
          fontWeight={fontWeight.SEMIBOLD}
          isEllipses
        />
      </Box>
      {onEdit ? (
        <button style={EDIT_BTN_STYLE} onClick={onEdit}>
          <IconComponent
            iconLabel={ICON_LABELS.EDIT}
            color={GREY.SECONDARY}
            fontSize="14px"
          />
          <TypographyInline
            label="Edit"
            size="xs"
            color={GREY.SECONDARY}
            fontWeight={fontWeight.MEDIUM}
          />
        </button>
      ) : null}
    </Box>
    <Box style={CARD_BODY_STYLE}>{children}</Box>
  </Box>
);

const Row = ({ label, children }) => (
  <Box style={ROW_STYLE}>
    <Box style={LABEL_COL_STYLE}>
      <TypographyInline
        label={label}
        size="sm"
        color={GREY.SECONDARY}
        fontWeight={fontWeight.REGULAR}
      />
    </Box>
    <Box style={VALUE_COL_STYLE}>{children}</Box>
  </Box>
);

const ValueText = ({ value, placeholder = "Not set" }) => (
  <TypographyInline
    label={value || placeholder}
    size="sm"
    color={value ? GREY.PRIMARY : GREY.TERTIARY}
    fontWeight={value ? fontWeight.MEDIUM : fontWeight.REGULAR}
  />
);

const AvatarStack = ({ emails }) => {
  const visible = emails.slice(0, AVATAR_GROUP_LIMIT);
  const overflow = emails.length - visible.length;
  const stackItem = {
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    background: ACTIONS.DEFAULT,
    color: GREY.SECONDARY,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "10px",
    fontWeight: 700,
    textTransform: "uppercase",
    flexShrink: 0,
    border: `2px solid ${BASE.WHITE}`,
    boxShadow: "0 0 0 1px " + BORDER.DEFAULT,
  };
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {visible.map((email, idx) => (
        <Box
          key={email}
          style={{
            ...stackItem,
            marginLeft: idx === 0 ? 0 : "-7px",
            zIndex: AVATAR_GROUP_LIMIT - idx,
          }}
        >
          {initialOf(email)}
        </Box>
      ))}
      {overflow > 0 && (
        <Box style={{ ...stackItem, marginLeft: "-7px" }}>{`+${overflow}`}</Box>
      )}
    </Box>
  );
};

const GroupedSenderList = ({ groupBreakdown, signaturesByEmail }) => {
  // Auto-expand the first group only; users can open the others on demand.
  const [openSet, setOpenSet] = useState(() =>
    groupBreakdown[0] ? new Set([groupBreakdown[0]._id]) : new Set(),
  );

  const toggle = (gid) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(gid)) next.delete(gid);
      else next.add(gid);
      return next;
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        maxHeight: "320px",
        overflowY: "auto",
        border: `1px solid ${BORDER.LIGHT}`,
        borderRadius: "6px",
      }}
    >
      {groupBreakdown.map((g, gIdx) => {
        const isOpen = openSet.has(g._id);
        const isLastGroup = gIdx === groupBreakdown.length - 1;
        return (
          <Box
            key={g._id}
            sx={{
              display: "flex",
              flexDirection: "column",
              borderBottom: isLastGroup ? "none" : `1px solid ${BORDER.LIGHT}`,
            }}
          >
            <Box
              onClick={() => toggle(g._id)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 12px",
                background: ACTIONS.HOVER,
                borderBottom: isOpen
                  ? `1px solid ${BORDER.LIGHT}`
                  : "none",
                cursor: "pointer",
                userSelect: "none",
                "&:hover": { background: BASE.BACKGROUND_LIGHT },
              }}
            >
              <IconComponent
                iconLabel={
                  isOpen
                    ? ICON_LABELS.EXPAND_LESS
                    : ICON_LABELS.KEYBOARD_ARROW_RIGHT
                }
                color={GREY.SECONDARY}
                fontSize="16px"
              />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <TypographyInline
                  label={g.group_name}
                  size="xs"
                  color={GREY.PRIMARY}
                  fontWeight={fontWeight.SEMIBOLD}
                  isEllipses
                />
              </Box>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  background: BASE.WHITE,
                  color: GREY.SECONDARY,
                  fontSize: "10px",
                  fontWeight: 600,
                  padding: "1px 6px",
                  borderRadius: "10px",
                }}
              >
                {g.emails.length}
              </span>
            </Box>
            {isOpen &&
              g.emails.map((email, eIdx) => {
                const sig = signaturesByEmail?.[email];
                const isLastEmail = eIdx === g.emails.length - 1;
                return (
                  <Box
                    key={`${g._id}:${email}`}
                    style={{
                      ...SENDER_ROW_STYLE,
                      padding: "8px 12px",
                      borderBottom: isLastEmail
                        ? "none"
                        : `1px solid ${BORDER.LIGHT}`,
                    }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <TypographyInline
                        label={email}
                        size="sm"
                        color={GREY.PRIMARY}
                        fontWeight={fontWeight.MEDIUM}
                        isEllipses
                      />
                    </Box>
                    {sig?.label ? (
                      <Box
                        sx={{
                          minWidth: 0,
                          maxWidth: "60%",
                          textAlign: "right",
                        }}
                      >
                        <TypographyInline
                          label={sig.label}
                          size="xs"
                          color={GREY.TERTIARY}
                          fontWeight={fontWeight.REGULAR}
                          isEllipses
                        />
                      </Box>
                    ) : null}
                  </Box>
                );
              })}
          </Box>
        );
      })}
    </Box>
  );
};

const SenderList = ({ emails, signaturesByEmail, groupBreakdown }) => {
  const [expanded, setExpanded] = useState(false);

  if (!emails || emails.length === 0) {
    return <ValueText value="" placeholder="No sending accounts selected" />;
  }

  // When groups drove the selection, render a grouped list so the user can
  // tell which mailbox came from which group.
  const useGroupedView =
    Array.isArray(groupBreakdown) && groupBreakdown.length > 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
        }}
      >
        <AvatarStack emails={emails} />
        <TypographyInline
          label={`${emails.length} ${
            emails.length === 1 ? "sender" : "senders"
          } sending this campaign`}
          size="sm"
          color={GREY.PRIMARY}
          fontWeight={fontWeight.MEDIUM}
        />
        <Box
          sx={{
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: "2px",
            marginLeft: "auto",
            padding: "2px 6px",
            borderRadius: "4px",
          }}
          onClick={() => setExpanded((v) => !v)}
        >
          <TypographyInline
            label={expanded ? "Hide" : "View more"}
            size="xs"
            color={PRIMARY.W_500}
            fontWeight={fontWeight.MEDIUM}
          />
          <IconComponent
            iconLabel={
              expanded
                ? ICON_LABELS.EXPAND_LESS
                : ICON_LABELS.KEYBOARD_ARROW_RIGHT
            }
            color={PRIMARY.W_500}
            fontSize="14px"
          />
        </Box>
      </Box>

      {expanded &&
        (useGroupedView ? (
          <GroupedSenderList
            groupBreakdown={groupBreakdown}
            signaturesByEmail={signaturesByEmail}
          />
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              maxHeight: "240px",
              overflowY: "auto",
              border: `1px solid ${BORDER.LIGHT}`,
              borderRadius: "6px",
            }}
          >
            {emails.map((email, i) => {
              const sig = signaturesByEmail?.[email];
              const isLast = i === emails.length - 1;
              return (
                <Box
                  key={email}
                  style={{
                    ...SENDER_ROW_STYLE,
                    padding: "8px 12px",
                    borderBottom: isLast
                      ? "none"
                      : `1px solid ${BORDER.LIGHT}`,
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <TypographyInline
                      label={email}
                      size="sm"
                      color={GREY.PRIMARY}
                      fontWeight={fontWeight.MEDIUM}
                      isEllipses
                    />
                  </Box>
                  {sig?.label ? (
                    <Box
                      sx={{ minWidth: 0, maxWidth: "60%", textAlign: "right" }}
                    >
                      <TypographyInline
                        label={sig.label}
                        size="xs"
                        color={GREY.TERTIARY}
                        fontWeight={fontWeight.REGULAR}
                        isEllipses
                      />
                    </Box>
                  ) : null}
                </Box>
              );
            })}
          </Box>
        ))}
    </Box>
  );
};

const CampaignPreviewStep = ({
  classes,
  name,
  sendVia,
  isMultiEmailEsp,
  itemData,
  sequenceName,
  listName,
  blockedList,
  scheduleDate,
  timezone,
  getTimings,
  millisecondsToDateStringWithoutNumber,
  convertData,
  sequenceType,
  currentStep,
  totalSteps,
  icpPersonaEnabled,
  companyPersonaLabel,
  selectedSenderEmails = [],
  signaturesByEmail = {},
  groupBreakdown = null,
  listKeys = [],
  selectedCustomizationKeys = [],
  onJumpToStep = () => {},
}) => {
  const senderEmails = isMultiEmailEsp
    ? selectedSenderEmails
    : itemData?.email
      ? [itemData.email]
      : [];

  return (
    <Box>
      <div className={classes.div}>
        <TypographyInline
          size={"md"}
          color={GREY.PRIMARY}
          fontWeight={fontWeight.SEMIBOLD}
          label={STRINGS.PREVIEW}
        />
        <TypographyInline
          size={"sm"}
          color={GREY.SECONDARY}
          fontWeight={fontWeight.REGULAR}
          label={
            currentStep
              ? `Step ${currentStep} of ${totalSteps}`
              : STRINGS.STEP55
          }
        />
      </div>

      <Box
        sx={{
          padding: "16px",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        <StepCard
          stepNumber={1}
          title="Campaign Basic Details"
          onEdit={() => onJumpToStep(0)}
        >
          <Row label="Name">
            <ValueText value={name} />
          </Row>
          <Row label="Send via">
            <ValueText value={sendVia} />
          </Row>
          <Row label="Sending accounts">
            <SenderList
              emails={senderEmails}
              signaturesByEmail={signaturesByEmail}
              groupBreakdown={groupBreakdown}
            />
          </Row>
        </StepCard>

        <StepCard
          stepNumber={2}
          title="Sequence & List"
          onEdit={() => onJumpToStep(1)}
        >
          <Row label="Sequence">
            <ValueText value={sequenceName?.label} />
          </Row>
          <Row label="Contact list">
            <ValueText value={listName?.label} />
          </Row>
          {icpPersonaEnabled && companyPersonaLabel ? (
            <Row label="ICP persona">
              <ValueText value={companyPersonaLabel} />
            </Row>
          ) : null}
        </StepCard>

        <StepCard
          stepNumber={3}
          title={STRINGS.CUSTOMIZATION_KEYS}
          onEdit={() => onJumpToStep(2)}
        >
          <Row label="Keys in use">
            <SelectedKeyList
              keys={listKeys}
              selectedIds={selectedCustomizationKeys}
            />
          </Row>
        </StepCard>

        {/* <StepCard stepNumber={3} title="Skipped Contacts" onEdit={() => onJumpToStep(2)}>
          <Row label="Total">
            <ValueText value={`${blockedList?.length || 0} ${blockedList?.length === 1 ? STRINGS.CONTACT : STRINGS.CONTACTS}`} />
          </Row>
        </StepCard> */}

        {sequenceType === "manual" && (
          <StepCard
            stepNumber={4}
            title="Schedule & Timings"
            onEdit={() => onJumpToStep(3)}
          >
            <Row label="Start date">
              <ValueText
                value={
                  (scheduleDate &&
                    millisecondsToDateStringWithoutNumber(scheduleDate)) ||
                  ""
                }
              />
            </Row>
            <Row label="Timezone">
              <ValueText value={convertData(timezone?.[0]) || ""} />
            </Row>
            <Row label="Send window">
              {getTimings?.some((d) => d?.checked) ? (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "6px",
                  }}
                >
                  {getTimings
                    ?.filter((d) => d?.checked)
                    ?.map((weekDays, idx) => {
                      const dayLabel =
                        CAMPAIGN_CREATE_DAYS_LIST[weekDays?.day]?.day || "";
                      const startMin =
                        weekDays.startMinute < 10
                          ? `0${weekDays.startMinute}`
                          : weekDays.startMinute;
                      const endMin =
                        weekDays.endMinute < 10
                          ? `0${weekDays.endMinute}`
                          : weekDays.endMinute;
                      const time = `${weekDays.startTime}:${startMin}${weekDays.startType}-${weekDays.endTime}:${endMin}${weekDays.endType}`;
                      return (
                        <Box key={idx} style={DAY_PILL_STYLE}>
                          <TypographyInline
                            label={dayLabel}
                            size="xs"
                            color={GREY.PRIMARY}
                            fontWeight={fontWeight.MEDIUM}
                            isCapitalise
                          />
                          <TypographyInline
                            label={time}
                            size="xs"
                            color={GREY.SECONDARY}
                            fontWeight={fontWeight.REGULAR}
                          />
                        </Box>
                      );
                    })}
                </Box>
              ) : (
                <ValueText value="" placeholder="No days selected" />
              )}
            </Row>
          </StepCard>
        )}
      </Box>
    </Box>
  );
};

export default React.memo(CampaignPreviewStep);
